import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { MSpinner } from "components/Shared";
import { Button, TextField } from "react-md";
import StarRatings from "react-star-ratings";
import { isSatisfactionSurveyAvailable, setSatisfactionSurvey } from "requests/satisfactionSurvey/customerInsights";
import responseHandler from "responseHandler";
import { CustomerInsightsSurveyUpdateRequest } from "interfaces/Mueller.Business.Models";

interface Props {
  match: { params: { uId: string } },
  makeToast: (arg: string) => void,
}

let STATUS = {
  NONE: "NONE",
  FETCHING: "FETCHING",
  READY: "READY",
  RATED: "RATED",
  EXPIRED: "EXPIRED",
  ERROR: "ERROR",
};

const CustomerInsightsSurvey = (props: Props) => {
  const mounted = useRef(true);
  const { match: { params }, makeToast } = props;  
  const [status, setStatus] = useState(STATUS.NONE);

  const COMMENTS_MAXLENGTH = 1000;

  const starRatedColorConst ="#E03D4C";
  const starHoverColorConst ="#CA2030";

  const [surveyResponse, setSurveyResponse] = useState<CustomerInsightsSurveyUpdateRequest>({
    uId: params.uId,
    /** Timeliness score */
    timelinessScore: 0,
    /** Quality score */
    qualityScore: 0,
    /** Communication score */
    communicationScore: 0,
    overallScore: 0,
    /** Additional feedback */
    additionalFeedback: null
  });

  useEffect(() => {
    if(status === STATUS.NONE)
      fetchStatus();

    return () => { mounted.current = false; };
  }, []);

  const fetchStatus = () => {      
    isSatisfactionSurveyAvailable(
      params.uId, 
      (data) => { data && data.canCompleteSatisfactionSurvey ? setStatus(STATUS.READY) : setStatus(STATUS.EXPIRED); },
      () => { setStatus(STATUS.ERROR); },
      mounted
    );    
    setStatus(STATUS.FETCHING);
  };

  const updateSurveyResponse = (key : string, val : any) => {
    setSurveyResponse((prevSurveyResponse) => ({
      ...prevSurveyResponse,
      [key]: val
    }));
  }
  
  const submitFeedback = () => {  
    let passesValidation = null;
    let responseToSubmit = {...surveyResponse};    
    responseToSubmit.uId = params.uId;
    Object.keys(responseToSubmit).forEach((key) => {
      const element = document.getElementById(key);
      if (responseToSubmit[key] === null) {
        passesValidation = key;
        if (element) {
          element.classList.add("error-highlight"); // Add red text class
        }
      } else {
        if (element) {
          element.classList.remove("error-highlight"); // Remove red text class
        }
      }
    });

    if(passesValidation !== null){  
      const element = document.getElementById(passesValidation);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "center" });
      }      
      makeToast("Please fill out each question before submitting.");
      return;
    }
    
    setSatisfactionSurvey(params.uId, responseToSubmit, () => { 
      setStatus(STATUS.RATED); 
    }, () => setStatus(STATUS.READY), mounted);    
    setStatus(STATUS.FETCHING);
  };

  return (
    <div className="satisfaction-survey-root">
      {(status === STATUS.NONE || status === STATUS.FETCHING) && <MSpinner />}
      {status === STATUS.READY &&
        <>        
          <h3>How satisfied are you with Mueller Reports on the following factors?</h3>
          <div className="rating-tip">(1 - Very Poor, 2 - Poor, 3 - Fair, 4 - Good, 5 - Excellent)</div>                 
          <h4 id={"timelinessScore"}>Timeliness </h4>   
          <StarRatings 
            rating={surveyResponse.timelinessScore ?? 0}
            changeRating={(nextRating) => updateSurveyResponse("timelinessScore", nextRating)}
            starRatedColor={starRatedColorConst}
            starHoverColor={starHoverColorConst} 
          />                          
          <h4 id={"qualityScore"}>Quality</h4>  
          <StarRatings             
            rating={surveyResponse.qualityScore ?? 0}
            changeRating={(nextRating) => updateSurveyResponse("qualityScore", nextRating)}
            starRatedColor={starRatedColorConst}
            starHoverColor={starHoverColorConst}
          />  
          <h4 id={"communicationScore"}>Communication</h4>  
          <StarRatings             
            rating={surveyResponse.communicationScore ?? 0}
            changeRating={(nextRating) => updateSurveyResponse("communicationScore", nextRating)}
            starRatedColor={starRatedColorConst}
            starHoverColor={starHoverColorConst}
          />    
          <h4 id={"overallScore"}>Overall</h4>  
          <StarRatings             
            rating={surveyResponse.overallScore ?? 0}
            changeRating={(nextRating) => updateSurveyResponse("overallScore", nextRating)}
            starRatedColor={starRatedColorConst}
            starHoverColor={starHoverColorConst}
          />                                               
          <h4 id="additionalFeedback">Any additional feedback?</h4>                              
          <TextField            
            label=""
            maxLength={COMMENTS_MAXLENGTH}
            rows={2}
            onChange={(val) => updateSurveyResponse("additionalFeedback", val)}
          />          
          <h3>Thank you for your time!</h3>
          <Button primary raised onClick={submitFeedback}>Submit</Button>
        </>
      }
      {status === STATUS.RATED &&
        <div className="satisfaction-survey-complete">
          <h3>Thank you for your feedback!</h3>
          <a href="https://www2.muellerreports.com/contact-us/">
            <Button primary raised>Contact Us</Button>
          </a>
        </div>
      }
      {status === STATUS.EXPIRED &&
        <div className="satisfaction-survey-complete">
          <h3>This link has expired.  Please contact customer service with any additional feedback.</h3>
          <a href="https://www2.muellerreports.com/contact-us/">
            <Button primary raised>Contact Us</Button>
          </a>
        </div>
      }
      {status === STATUS.ERROR &&
        <div className="satisfaction-survey-complete">
          <h3>Oops!  You appear to have reached this page in error.</h3>
          <a href="https://www2.muellerreports.com/contact-us/">
            <Button primary raised>Contact Us</Button>
          </a>
        </div>
      }
    </div>  
  );
};

const mapDispatchToProps = dispatch => ({
  makeToast: text => {
    responseHandler.customToast(text, "error", dispatch);
  }
});

export default connect(null, mapDispatchToProps)(CustomerInsightsSurvey);